<template>
  <div class="relative">
    <!-- Enhanced Notification Bell Button (Minimal Design) -->
    <button @click="toggleNotifications"
      class="relative p-2.5 rounded-full text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-surface-100/80 dark:hover:bg-dark-700/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900"
      :class="{ 'text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/10': isOpen }">
      <span class="sr-only">View notifications</span>

      <!-- Icon Container -->
      <div class="relative">
        <i class="fas fa-bell text-xl"></i>

        <!-- Minimal Badge -->
        <transition name="badge-bounce">
          <span v-if="unreadCount > 0"
            class="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-dark-800">
            {{ unreadCount > 9 ? '!' : unreadCount }}
          </span>
        </transition>
      </div>
    </button>

    <!-- Notifications Panel -->
    <transition name="panel-slide">
      <div v-if="isOpen"
        class="absolute right-0 mt-3 w-96 bg-white dark:bg-dark-800 rounded-xl shadow-lg ring-1 ring-gray-200 dark:ring-dark-700 focus:outline-none overflow-hidden z-50 origin-top-right border border-gray-200 dark:border-dark-700">
        <!-- Header -->
        <div
          class="px-5 py-4 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">Notifications</h3>
              <span v-if="unreadCount > 0"
                class="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                {{ unreadCount }} new
              </span>
            </div>
            <div class="flex items-center space-x-2">
              <select v-model="currentFilter" @change="applyFilter"
                class="text-xs border-none bg-surface-100/50 dark:bg-dark-700/50 rounded-lg px-2 py-1 text-gray-600 dark:text-gray-300 focus:ring-1 focus:ring-primary-500 cursor-pointer hover:bg-surface-200/50 transition-colors">
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="payment">Payments</option>
                <option value="order">Orders</option>
              </select>

              <button v-if="filteredNotifications.length > 0" @click="handleMarkAllAsRead"
                class="p-1 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                title="Mark all as read">
                <i class="fas fa-check-double text-xs"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- content area -->
        <div class="bg-white dark:bg-dark-800">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-8">
            <div class="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
              <i class="fas fa-spinner fa-spin text-primary-500"></i>
              <span class="text-sm">Loading...</span>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="px-6 py-8 text-center">
            <p class="text-red-500 text-sm mb-2">{{ error }}</p>
            <button @click="retryFetch" class="text-xs text-primary-600 underline">Retry</button>
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredNotifications.length === 0" class="px-6 py-12 text-center">
            <div
              class="w-12 h-12 rounded-full bg-gray-50 dark:bg-dark-700 mx-auto flex items-center justify-center mb-3 text-gray-400">
              <i class="far fa-bell text-xl"></i>
            </div>
            <p class="text-gray-500 dark:text-gray-400 text-sm">No notifications found</p>
          </div>

          <!-- Enhanced Notifications List -->
          <div v-else class="max-h-80 overflow-y-auto custom-scrollbar">
            <div v-for="notification in paginatedNotifications" :key="notification.id"
              @click="handleNotificationClick(notification)"
              class="group relative flex items-start gap-x-3 px-4 py-3 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 border-b border-gray-100 dark:border-dark-600 cursor-pointer transition-all duration-200 ease-in-out"
              :class="{
                'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-l-blue-500 dark:border-l-blue-400 shadow-sm': !notification.is_read,
                'bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700': notification.is_read
              }">
              <!-- Notification Icon -->
              <div class="flex-shrink-0 mt-0.5">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center text-sm shadow-sm border border-white dark:border-dark-600"
                  :class="getNotificationIconClass(notification)">
                  <i :class="getNotificationIcon(notification)"></i>
                </div>
              </div>

              <!-- Notification Content -->
              <div class="flex-1 min-w-0">
                <!-- Enhanced message with better formatting -->
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <!-- Title if available -->
                    <h4 v-if="notification.title" class="text-sm font-semibold text-gray-900 dark:text-dark-100 mb-1">
                      {{ notification.title }}
                    </h4>

                    <p class="text-sm text-gray-700 dark:text-dark-300 leading-relaxed"
                      :class="notification.title ? '' : 'font-medium'">
                      {{ getEnhancedMessage(notification) }}
                    </p>

                    <!-- Additional context with better styling -->
                    <div v-if="getNotificationContext(notification)" class="mt-2">
                      <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                        {{ getNotificationContext(notification) }}
                      </span>
                    </div>
                  </div>

                  <!-- Action button -->
                  <button v-if="!notification.is_read" @click.stop="handleMarkAsRead(notification.id)"
                    class="opacity-0 group-hover:opacity-100 ml-2 p-1.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800 transition-all duration-200"
                    title="Mark as read">
                    <i class="fas fa-check text-xs"></i>
                  </button>
                </div>

                <!-- Time and status -->
                <div class="mt-3 flex items-center justify-between">
                  <p class="text-xs text-gray-500 dark:text-dark-400 flex items-center font-medium">
                    <i class="fas fa-clock mr-1.5 text-gray-400 dark:text-dark-500"></i>
                    {{ formatDate(notification.created_at) }}
                  </p>

                  <!-- Clickable indicator -->
                  <div v-if="isNotificationClickable(notification)"
                    class="text-xs text-blue-600 dark:text-blue-400 flex items-center font-medium opacity-75 group-hover:opacity-100 transition-opacity">
                    <span>View details</span>
                    <i class="fas fa-arrow-right ml-1.5"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination Footer -->
          <div v-if="shouldShowPagination"
            class="bg-gray-50 dark:bg-dark-700 px-6 py-3 border-t border-gray-100 dark:border-dark-600">
            <div class="flex items-center justify-between">
              <span class="text-xs text-gray-500 dark:text-dark-400">
                Showing {{ Math.min(currentPage * itemsPerPage, filteredNotifications.length) }} of {{
                  filteredNotifications.length }}
              </span>

              <div class="flex items-center space-x-2">
                <button @click="previousPage" :disabled="currentPage === 1"
                  class="px-2 py-1 text-xs bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-dark-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  Previous
                </button>

                <span class="text-xs text-gray-500 dark:text-dark-400">
                  {{ currentPage }} / {{ totalPages }}
                </span>

                <button @click="nextPage" :disabled="currentPage >= totalPages"
                  class="px-2 py-1 text-xs bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-dark-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  Next
                </button>
              </div>
            </div>

            <!-- Load more button for older notifications -->
            <div v-if="shouldShowLoadMore" class="mt-2 text-center">
              <button @click="loadMore" :disabled="loadingMore"
                class="w-full text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 py-2 px-4 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                <i v-if="loadingMore" class="fas fa-spinner fa-spin mr-1"></i>
                {{ loadingMore ? 'Loading...' : 'Load older notifications' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Notification Preview Modals -->
    <Modal v-if="showOrderModal" v-model="showOrderModal" size="xl">
      <template #title>Order Details</template>
      <template #content>
        <OrderPreview :order-id="selectedOrderId" />
      </template>
    </Modal>

    <Modal v-if="showPaymentModal" v-model="showPaymentModal" size="lg">
      <template #title>Payment Details</template>
      <template #content>
        <PaymentPreview :payment-id="selectedPaymentId" />
      </template>
    </Modal>

    <Modal v-if="showExpenseModal" v-model="showExpenseModal" size="xl">
      <template #title>Expense Details</template>
      <template #content>
        <ExpensePreview v-model="showExpenseModal" :expense="selectedExpense" />
      </template>
    </Modal>

    <Modal v-if="showQuotationModal" v-model="showQuotationModal" size="lg">
      <template #title>Quotation Details</template>
      <template #content>
        <QuotationPreview :quotation="selectedQuotation" />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationsStore } from '../stores/notifications';
import { storeToRefs } from 'pinia';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/composables/useToast';
import { useApi } from '@/composables/useApi';

// Modal imports
import Modal from '@/components/common/Modal.vue';
import OrderPreview from '@/components/orders/OrderPreview.vue';
import PaymentPreview from '@/components/payments/PaymentPreview.vue';
import ExpensePreview from '@/components/expenses/ExpensePreview.vue';
import QuotationPreview from '@/components/approvals/QuotationPreview.vue';

const router = useRouter();
const api = useApi();
const isOpen = ref(false);
const currentFilter = ref('all');
const currentFilters = ref({}); // Track current filter parameters
const currentPage = ref(1);
const itemsPerPage = 20;
const loadingMore = ref(false);
const totalNotificationsLoaded = ref(0);
const hasMoreData = ref(true);

// Modal states
const showOrderModal = ref(false);
const showPaymentModal = ref(false);
const showExpenseModal = ref(false);
const showQuotationModal = ref(false);

// Selected items for modals
const selectedOrderId = ref(null);
const selectedPaymentId = ref(null);
const selectedExpense = ref(null);
const selectedQuotation = ref(null);

const store = useNotificationsStore();
const { notifications, unreadCount, loading, error } = storeToRefs(store);
const toast = useToast();

// Computed properties
const hasUnread = computed(() => unreadCount.value > 0);

// Remove client-side filtering - we'll fetch filtered results from server
const filteredNotifications = computed(() => {
  return notifications.value || [];
});

const totalPages = computed(() => Math.ceil(filteredNotifications.value.length / itemsPerPage));

const paginatedNotifications = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredNotifications.value.slice(start, end);
});

// Show pagination footer when we have enough items
const shouldShowPagination = computed(() => {
  return filteredNotifications.value.length > itemsPerPage || shouldShowLoadMore.value;
});

// Show load more button when:
// 1. We're on the last page of current results
// 2. And there might be more data from the server
// 3. AND we have at least one full page worth of notifications
const shouldShowLoadMore = computed(() => {
  const shouldShow = currentPage.value === totalPages.value &&
    hasMoreData.value &&
    notifications.value?.length >= itemsPerPage;

  console.log('[NotificationBell] shouldShowLoadMore check:', {
    currentPage: currentPage.value,
    totalPages: totalPages.value,
    hasMoreData: hasMoreData.value,
    filteredLength: filteredNotifications.value.length,
    itemsPerPage,
    shouldShow
  });

  return shouldShow;
});

// Methods
const toggleNotifications = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value && unreadCount.value > 0) {
    // Play subtle notification sound
    playNotificationSound();
  }
};

const applyFilter = async () => {
  currentPage.value = 1; // Reset to first page when filtering
  hasMoreData.value = true; // Reset the "has more data" flag

  // Map filter values to API parameters
  let filterType = null;
  switch (currentFilter.value) {
    case 'unread':
      filterType = null; // We'll handle unread differently
      break;
    case 'payment':
      filterType = 'payment';
      break;
    case 'order':
      filterType = 'order';
      break;
    case 'expense':
      filterType = 'expense';
      break;
    case 'quotation':
      filterType = 'quotation';
      break;
    default:
      filterType = null; // 'all'
  }

  // Store current filters for use in loadMore
  currentFilters.value = {
    type: filterType,
    unread: currentFilter.value === 'unread' ? true : undefined
  };

  console.log('🔍 Applying filter:', currentFilter.value, 'API filter type:', filterType, 'Current filters:', currentFilters.value);

  try {
    // Fetch filtered notifications from server
    await store.fetchNotifications(1, itemsPerPage, currentFilters.value);

    console.log('✅ Filter applied successfully. Loaded notifications:', notifications.value?.length);
  } catch (error) {
    console.error('❌ Failed to apply filter:', error);
    toast.error('Failed to load filtered notifications');
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const loadMore = async () => {
  if (!hasMoreData.value || loadingMore.value) return;

  loadingMore.value = true;
  try {
    const currentTotal = notifications.value.length;
    console.log(`[NotificationBell] Loading more notifications. Current total: ${currentTotal}, filters:`, currentFilters.value);

    const hasMore = await store.fetchMoreNotifications(currentTotal, itemsPerPage, currentFilters.value);
    hasMoreData.value = hasMore;

    console.log(`[NotificationBell] Load more completed. New total: ${notifications.value.length}, hasMore: ${hasMore}`);

    // If we got new notifications, they'll be automatically reflected in the computed properties
  } catch (err) {
    console.error('[NotificationBell] Load more failed:', err);
    toast.error('Failed to load more notifications');
  } finally {
    loadingMore.value = false;
  }
};

const retryFetch = async () => {
  try {
    await store.fetchNotifications();
  } catch (err) {
    toast.error('Failed to refresh notifications');
  }
};

const getNotificationIcon = (notification) => {
  if (notification.type?.includes('payment')) return 'fas fa-credit-card';
  if (notification.type?.includes('order')) return 'fas fa-shopping-cart';
  if (notification.type?.includes('expense')) return 'fas fa-receipt';
  if (notification.type?.includes('quotation')) return 'fas fa-file-invoice';
  return 'fas fa-bell';
};

const getNotificationIconClass = (notification) => {
  if (notification.type?.includes('payment')) {
    return notification.type.includes('approved')
      ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
      : 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400';
  }
  if (notification.type?.includes('order')) return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400';
  if (notification.type?.includes('expense')) {
    return notification.type.includes('approved')
      ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
      : 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400';
  }
  if (notification.type?.includes('quotation')) return 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400';
  return 'bg-gray-100 dark:bg-dark-600 text-gray-600 dark:text-dark-400';
};

const getEnhancedMessage = (notification) => {
  // Enhanced message formatting based on notification type
  const data = notification.data || {};

  if (notification.type?.includes('payment')) {
    if (data.order_number) {
      return notification.message.replace(/Payment #\d+/, `Payment for Order #${data.order_number}`);
    }
  }

  if (notification.type?.includes('expense') && notification.message.includes('₱0.00')) {
    // Fix the ₱0.00 issue by using the correct amount from data
    const amount = data.amount || data.total_cost || '0.00';
    return notification.message.replace('₱0.00', `₱${parseFloat(amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`);
  }

  return notification.message;
};

const getNotificationContext = (notification) => {
  const data = notification.data || {};

  if (data.order_number) return `Order #${data.order_number}`;
  if (data.quotation_number) return `Quotation #${data.quotation_number}`;
  if (data.expense_id) return `Expense #${data.expense_id}`;
  if (data.payment_id) return `Payment #${data.payment_id}`;

  return null;
};

const isNotificationClickable = (notification) => {
  const data = notification.data || {};
  return !!(data.order_id || data.payment_id || data.expense_id || data.quotation_id);
};

const handleNotificationClick = async (notification) => {
  // Mark as read if not already
  if (!notification.is_read) {
    await handleMarkAsRead(notification.id);
  }

  const data = notification.data || {};

  try {
    // Use entity_id and entity_type from notification object
    const entityId = notification.entity_id;
    const entityType = notification.entity_type;

    console.log('🔔 Notification clicked:', {
      notification,
      data,
      type: notification.type,
      entityId,
      entityType
    });

    // Check if we can open a modal based on the notification entity data
    if (entityType === 'order' && entityId) {
      console.log('📦 Opening order modal for ID:', entityId);
      selectedOrderId.value = entityId;
      showOrderModal.value = true;
      isOpen.value = false; // Close notification panel
      return;
    }

    if (entityType === 'payment' && entityId) {
      console.log('💰 Opening payment modal for ID:', entityId);
      selectedPaymentId.value = entityId;
      showPaymentModal.value = true;
      isOpen.value = false;
      return;
    }

    if (entityType === 'expense' && entityId) {
      // Fetch expense details for the modal
      console.log('💸 Fetching expense details for ID:', entityId);
      try {
        // Use query parameter for expenses API since it doesn't have individual endpoint
        const response = await api.get(`/expenses?id=${entityId}`);
        // The API returns an array, so get the first item
        const expenseData = response.data?.data?.[0];
        if (expenseData) {
          selectedExpense.value = expenseData;
          showExpenseModal.value = true;
          isOpen.value = false;
          return;
        } else {
          console.error('❌ Expense not found in response');
          toast.error('Expense not found');
          isOpen.value = false;
          return;
        }
      } catch (error) {
        console.error('❌ Failed to fetch expense details:', error);
        toast.error('Failed to load expense details');
        // Don't fall through to navigation - just close panel
        isOpen.value = false;
        return;
      }
    }

    if (entityType === 'quotation' && entityId) {
      // Fetch quotation details for the modal
      console.log('📋 Fetching quotation details for ID:', entityId);
      try {
        // Use correct quotations endpoint
        const response = await api.get(`/quotations/${entityId}`);
        if (response.data?.success && response.data?.data) {
          selectedQuotation.value = response.data.data;
        } else if (response.data) {
          selectedQuotation.value = response.data;
        } else {
          throw new Error('Invalid quotation response format');
        }
        showQuotationModal.value = true;
        isOpen.value = false;
        return;
      } catch (error) {
        console.error('❌ Failed to fetch quotation details:', error);
        toast.error('Failed to load quotation details');
        // Don't fall through to navigation - just close panel
        isOpen.value = false;
        return;
      }
    }

    // Fallback: Check data object for specific IDs (legacy support)
    if (data.order_id) {
      console.log('📦 Using legacy order_id from data:', data.order_id);
      selectedOrderId.value = data.order_id;
      showOrderModal.value = true;
      isOpen.value = false;
      return;
    }

    if (data.payment_id) {
      console.log('💰 Using legacy payment_id from data:', data.payment_id);
      selectedPaymentId.value = data.payment_id;
      showPaymentModal.value = true;
      isOpen.value = false;
      return;
    }

    console.log('🔄 No modal data found, using fallback navigation');

    // Only use fallback navigation if no modal-worthy IDs were found
    if (data.navigate_to) {
      console.log('🚀 Navigating to:', data.navigate_to);
      router.push(data.navigate_to);
    } else if (notification.type?.includes('payment')) {
      router.push('/payments');
    } else if (notification.type?.includes('order')) {
      router.push('/orders');
    } else if (notification.type?.includes('expense')) {
      router.push('/expenses');
    } else if (notification.type?.includes('quotation')) {
      router.push('/quotations');
    }

    // Close the notification panel
    isOpen.value = false;

  } catch (err) {
    console.error('Error in notification click handler:', err);
    toast.error('Unable to navigate to details');
    isOpen.value = false;
  }
};

const handleMarkAsRead = async (notificationId) => {
  try {
    await store.markAsRead(notificationId);
  } catch (err) {
    toast.error('Failed to mark notification as read');
  }
};

const handleMarkAllAsRead = async () => {
  try {
    await store.markAllAsRead();
    toast.success('All notifications marked as read');
  } catch (err) {
    toast.error('Failed to mark all notifications as read');
  }
};

const formatDate = (date) => {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
};

const playNotificationSound = () => {
  try {
    // Subtle notification sound
    const audio = new Audio('/notification-subtle.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignore audio play errors (browser restrictions)
    });
  } catch (error) {
    // Ignore audio errors
  }
};

// Close notifications panel when clicking outside
const handleClickOutside = (event) => {
  if (isOpen.value && !event.target.closest('.relative')) {
    isOpen.value = false;
  }
};

// Handle new notification events
const handleNewNotification = (event) => {
  const { notification } = event.detail;
  console.log('New notification received:', notification);

  // Add visual feedback for new notifications
  if (notification && !notification.is_read) {
    playNotificationSound();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('new-notification', handleNewNotification);
  // Set initial filter state
  applyFilter();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('new-notification', handleNewNotification);
});

// Watch for filter changes to reset pagination and apply filters
watch(currentFilter, () => {
  currentPage.value = 1;
  applyFilter(); // Apply filter when selection changes
});
</script>

<style scoped>
/* Enhanced animations */
.badge-bounce-enter-active {
  animation: badge-bounce-in 0.5s ease-out;
}

.badge-bounce-leave-active {
  animation: badge-bounce-out 0.3s ease-in;
}

@keyframes badge-bounce-in {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }

  50% {
    transform: scale(1.2) rotate(180deg);
  }

  100% {
    transform: scale(1) rotate(360deg);
    opacity: 1;
  }
}

@keyframes badge-bounce-out {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(0);
    opacity: 0;
  }
}

.panel-slide-enter-active {
  animation: panel-slide-in 0.3s ease-out;
}

.panel-slide-leave-active {
  animation: panel-slide-out 0.2s ease-in;
}

@keyframes panel-slide-in {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes panel-slide-out {
  0% {
    transform: translateY(0);
    opacity: 1;
  }

  100% {
    transform: translateY(-10px);
    opacity: 0;
  }
}

/* Custom scrollbar for notifications list */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar */
.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: #374151;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.max-h-80::-webkit-scrollbar {
  width: 6px;
}

.max-h-80::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.max-h-80::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.max-h-80::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dark mode scrollbar for max-h-80 */
.dark .max-h-80::-webkit-scrollbar-track {
  background: #374151;
}

.dark .max-h-80::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .max-h-80::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
